/**
 * It checks if there are any backup coordinators installed.
 * @param {BackupCoordinatorStatus} backupStatus - BackupCoordinatorStatus - the status of the backup coordinator
 * @returns A function that returns a boolean value.
 */
import i18n from '@/plugins/i18n';
import COLORS from '@/design-system/colors.json';
import { BackupCoordinatorStatus } from './backupStatus.types';

export const STATUS_COLOR_MAP: Record<string, string> = {
  Yellow: COLORS['System/app_warning'],
  Red: COLORS['System/app_error'],
  Gray: COLORS['System/app_grey_76'],
  White: COLORS['System/app_white'],
  Green: COLORS['System/app_primary'],
  GraySeparator: COLORS['Text/text_lowest'],
};

/**
 * It returns a boolean value based on the length of the array.
 * @param {BackupCoordinatorStatus} backupStatus - BackupCoordinatorStatus - the status of the backup coordinator
 */
export const getBulletColorNameBackup = (backupStatus: BackupCoordinatorStatus) => {
  let bulletColor = STATUS_COLOR_MAP.Gray;
  if (backupStatus === BackupCoordinatorStatus.Incompatible) bulletColor = STATUS_COLOR_MAP.Red;
  if (backupStatus === BackupCoordinatorStatus.Down) bulletColor = STATUS_COLOR_MAP.White;
  if (backupStatus === BackupCoordinatorStatus.StandBy) bulletColor = STATUS_COLOR_MAP.Gray;

  return bulletColor;
};

export const getBulletColorNamePrimary = () => STATUS_COLOR_MAP.Green;

/**
 * It returns a string based on the value of the `backupStatus` parameter
 * @param {BackupCoordinatorStatus} backupStatus - The status of the backup coordinator.
 * @returns A string
 */
export const getPrimaryCoordinatorTextAppend = (backupStatus: BackupCoordinatorStatus) => {
  if (
    backupStatus === BackupCoordinatorStatus.StandBy ||
    backupStatus === BackupCoordinatorStatus.Incompatible ||
    backupStatus === BackupCoordinatorStatus.NotConfigured ||
    backupStatus === BackupCoordinatorStatus.Down
  ) {
    return i18n.global.t('generic.backupCoordinator.statusTextByType.active');
  }
  return i18n.global.t('generic.backupCoordinator.statusTextByType.notActive');
};

/**
 * It returns a string based on the value of the `backupStatus` parameter
 * @param {BackupCoordinatorStatus} backupStatus - The status of the backup coordinator.
 * @returns A function that returns a string.
 */
export const getTooltipTitle = (backupStatus: BackupCoordinatorStatus) => {
  if (backupStatus === BackupCoordinatorStatus.Down) {
    return i18n.global.t('generic.backupCoordinator.statusTooltip.unreachable');
  }
  if (backupStatus === BackupCoordinatorStatus.Incompatible) {
    return i18n.global.t('generic.backupCoordinator.statusTooltip.incompatible');
  }
  return i18n.global.t('generic.backupCoordinator.statusTooltip.notConfigured');
};

/**
 * If the backup status is not configured, return the tooltip anchor as top right, otherwise return top left
 * @param {BackupCoordinatorStatus} backupStatus - BackupCoordinatorStatus
 */
export const getTooltipAnchor = (backupStatus: BackupCoordinatorStatus) => (backupStatus === BackupCoordinatorStatus.NotConfigured ? 'top right' : 'top left');

/**
 * If the backup status is down, return 400, otherwise return undefined
 * @param {BackupCoordinatorStatus} backupStatus - BackupCoordinatorStatus
 */
export const getTooltipWidth = (backupStatus: BackupCoordinatorStatus) => (backupStatus === BackupCoordinatorStatus.Down ? 400 : undefined);

/**
 * It takes a string as an argument and returns a string
 * @param {BackupCoordinatorStatus} backupStatus - BackupCoordinatorStatus
 * @returns A string
 */
export const getBackupCoordinatorTextAppend = (backupStatus: BackupCoordinatorStatus) => {
  let textToAppend;
  switch (backupStatus) {
    case BackupCoordinatorStatus.StandBy:
      textToAppend = i18n.global.t('generic.backupCoordinator.statusTextByType.standby');
      break;
    case BackupCoordinatorStatus.Incompatible:
      textToAppend = i18n.global.t('generic.backupCoordinator.statusTextByType.incompatible');
      break;
    case BackupCoordinatorStatus.NotConfigured:
      textToAppend = i18n.global.t('generic.backupCoordinator.statusTextByType.notConfigured');
      break;
    case BackupCoordinatorStatus.Down:
      textToAppend = i18n.global.t('generic.backupCoordinator.statusTextByType.unreachable');
      break;
    default:
      textToAppend = 'unknown';
  }
  return textToAppend;
};

/**
 * It checks if there are any backup coordinators installed
 * @param {String} backupCoordinatorsNames - A comma-separated list of the names of the machines that are running the
 * backup coordinator.
 * @param {String} backupCoordinatorsHosts - The IP addresses of the backup coordinators.
 * @returns A boolean value.
 */
export const isAnyBackupInstalled = (backupCoordinatorsNames: String, backupCoordinatorsHosts: String) => {
  const machinesNames = [...backupCoordinatorsNames.split(',')];
  const machinesHosts = [...backupCoordinatorsHosts.split(',')];
  return backupCoordinatorsNames.length > 0 && machinesNames.length > 0 && backupCoordinatorsHosts.length > 0 && machinesHosts.length > 0;
};
